import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { Button } from '@99designs/common/components/Button';
import { Icon } from '@99designs/common/components/Icon';
import { ButtonGroup, ButtonGroupItem } from '@99designs/common/components/ButtonGroup';
import { FixedViewportElement } from '@99designs/common/components/FixedViewportElement';
import { Matrix, MatrixItem } from 'contest/components/matrix';
import EntryPlaceholder from './entry-placeholder';
import { times } from 'lodash';
import pluralSelect from '../../../utils/plural-select';
import ConfirmationPopup from './confirmation-popup';
import DrawerTransition from 'contest/components/transitions/drawer-transition';

class SelectWinnerFullscreenDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isActive: false,
        };
    }

    render() {
        return (
            <DrawerTransition in={this.state.isActive}>
                {this.state.isActive && (
                    <FixedViewportElement>{this.renderDrawerContent()}</FixedViewportElement>
                )}
                {!this.state.isActive && this.renderDrawerContent()}
            </DrawerTransition>
        );
    }

    renderDrawerContent() {
        const entryCount = this.props.entries.length;
        return (
            <div data-fullscreen-drawer>
                <div
                    className="fullscreen-drawer__header"
                    onClick={() => this.setState({ isActive: !this.state.isActive })}
                >
                    <span className="fullscreen-drawer__header__title">
                        <FormattedMessage
                            id={pluralSelect(entryCount, {
                                one: 'select_winner_fullscreen_drawer_header_one',
                                other: 'select_winner_fullscreen_drawer_header_other',
                            })}
                            values={{ entryCount }}
                        />
                    </span>
                    {this.renderEntryCount(entryCount)}
                    <Icon variations={[this.state.isActive ? 'down-2' : 'up-2']} />
                </div>
                <div className="fullscreen-drawer__content">
                    <div className="fullscreen-drawer__content__inner">
                        <Matrix small={3} medium={4}>
                            {times(this.props.entries.length + 1, (index) => (
                                <MatrixItem key={`item-${index}`}>
                                    {this.renderEntry(index)}
                                </MatrixItem>
                            ))}
                        </Matrix>
                        <div className="fullscreen-drawer__content__footer">
                            {this.renderButtons(this.props.buttonLayout)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderEntryCount(entryCount) {
        if (entryCount) {
            return (
                <ConfirmationPopup
                    isActive={this.props.entriesNeedingPopup.length > 0}
                    variations={['small']}
                    content={<FormattedMessage id="select_winner_drawer_confirmation" />}
                >
                    <span className="fullscreen-drawer__header__count">({entryCount})</span>
                </ConfirmationPopup>
            );
        }
    }

    renderButtons(type) {
        const entryCount = this.props.entries.length;
        return (
            <ButtonGroup variations={[type]}>
                {this.renderAdditionalWinnerPrice()}
                <ButtonGroupItem>
                    <Button
                        variations={['fluid', 'primary']}
                        onClick={this.props.onConfirm}
                        isDisabled={!this.props.entries.length}
                    >
                        <FormattedMessage
                            id={pluralSelect(entryCount, {
                                zero: 'select_winner_fullscreen_drawer_button_confirm_zero',
                                one: 'select_winner_fullscreen_drawer_button_confirm_one',
                                other: 'select_winner_fullscreen_drawer_button_confirm_other',
                            })}
                        />
                    </Button>
                </ButtonGroupItem>
                <ButtonGroupItem>
                    <Button
                        variations={['fluid', 'secondary']}
                        onClick={() => this.setState({ isActive: false })}
                    >
                        <FormattedMessage id="select_winner_fullscreen_drawer_button_cancel" />
                    </Button>
                </ButtonGroupItem>
            </ButtonGroup>
        );
    }

    renderAdditionalWinnerPrice() {
        const winnerCount = this.props.entries.length;
        if (winnerCount > 1) {
            return (
                <span style={{ paddingRight: '15px' }}>
                    <FormattedMessage
                        id={pluralSelect(winnerCount, {
                            one: 'select_winner_drawer_additional_winner_one',
                            other: 'select_winner_drawer_additional_winner_other',
                        })}
                    />
                    {' ' + this.props.additionalWinnerCostTotal}
                </span>
            );
        }
    }

    renderEntry(index) {
        if (index < this.props.entries.length) {
            const entry = this.props.entries[index];
            return (
                <div className="drawer-item drawer-item--fluid drawer-item--with-shadow">
                    <div className="drawer-item__content">
                        <img src={entry.imageUrl} />
                    </div>
                    <div className="drawer-item__entry-owner">
                        {`#${entry.contestEntryId}`}
                        <br />
                        <FormattedHTMLMessage
                            id="select_winner_drawer_item_label"
                            values={{ userName: entry.user.displayName }}
                        />
                    </div>
                    <div className="drawer-item__remove">
                        <button
                            className="solid-icon-button"
                            onClick={() => this.props.onRemoveWinner(index)}
                        >
                            <Icon type={'x-3'} />
                        </button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="drawer-item drawer-item--fluid">
                    <EntryPlaceholder size={48} />
                </div>
            );
        }
    }
}

SelectWinnerFullscreenDrawer.propTypes = {
    entries: PropTypes.arrayOf(
        PropTypes.shape({
            designId: PropTypes.number.isRequired,
            imageUrl: PropTypes.string.isRequired,
            contestEntryId: PropTypes.number.isRequired,
            user: PropTypes.shape({
                displayName: PropTypes.string.isRequired,
            }).isRequired,
        })
    ).isRequired,
    entriesNeedingPopup: PropTypes.array.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onRemoveWinner: PropTypes.func.isRequired,
    additionalWinnerCostTotal: PropTypes.string.isRequired,
    buttonLayout: PropTypes.oneOf(['fluid', 'stacked']).isRequired,
};

export default SelectWinnerFullscreenDrawer;



// WEBPACK FOOTER //
// ./src/web/js/contest/apps/entries/components/select-winner-fullscreen-drawer.jsx